import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, API } from '../utils/constants';
import { fmtFecha } from '../utils/helpers';
import { hoyISO, descargaActiva, bloqueoActivo } from '../utils/recovery';
import LanguageSwitcher from '../components/LanguageSwitcher';

export const DEFAULT_SETTINGS = {
  nombre: '',
  peso: '',
  altura: '',
  edad: '',
  goal_kcal: 2100,
  goal_proteina: 175,
  goal_carbos: 210,
  goal_grasa: 60,
  nivel_actividad: 'activo',
  descanso_defecto: 90,
  vibracion: true,
  notificaciones: true,
  accent_color: '#fd8b00',
  tema: 'aurora',
  bg_tint: 'purple',
  shake_undo: false,
  bloquear_rotacion: true,
  // Cuidado y recuperación
  bloqueo_progresion_hasta: '',   // fecha fin del bloqueo de progresión ('' = inactivo)
  descarga_hasta: '',             // fecha fin del periodo de descarga ('' = inactivo)
  descarga_pct: 30,               // % de reducción de carga durante descarga
  bloqueo_baseline: null,         // snapshot { exName: {weight, reps, sets} } al activar
};

const ACTIVITY_LEVELS = [
  { id: 'sedentario', ratios: { p: 0.30, c: 0.40, g: 0.30 } },
  { id: 'activo',     ratios: { p: 0.25, c: 0.45, g: 0.30 } },
  { id: 'intenso',    ratios: { p: 0.25, c: 0.50, g: 0.25 } },
  { id: 'elite',      ratios: { p: 0.30, c: 0.45, g: 0.25 } },
];

function calcMacros(kcal, nivelId) {
  const lv = ACTIVITY_LEVELS.find(l => l.id === nivelId) || ACTIVITY_LEVELS[1];
  return {
    proteina: Math.round(kcal * lv.ratios.p / 4),
    carbos:   Math.round(kcal * lv.ratios.c / 4),
    grasa:    Math.round(kcal * lv.ratios.g / 9),
  };
}

const BG_TINT_OPTIONS = [
  { id: 'purple', color: '#9b59b6', label: 'Violeta' },
  { id: 'green',  color: '#39FF14', label: 'Verde' },
  { id: 'orange', color: '#fd8b00', label: 'Naranja' },
  { id: 'blue',   color: '#00daf3', label: 'Azul' },
  { id: 'red',    color: '#e74c3c', label: 'Rojo' },
  { id: 'cyan',   color: '#00ffd5', label: 'Cian' },
  { id: 'gold',   color: '#f1c40f', label: 'Dorado' },
  { id: 'pink',   color: '#ff3d9a', label: 'Rosa' },
];

const ACCENT_OPTIONS = [
  { label: 'Naranja', value: '#ff6b35' },
  { label: 'Azul',   value: '#4fc3f7' },
  { label: 'Verde',  value: '#2ecc71' },
  { label: 'Púrpura',value: '#9b59b6' },
  { label: 'Teal',   value: '#1abc9c' },
  { label: 'Rosa',   value: '#ff2d78' },
];

const TEMA_OPTIONS = [
  { label: 'Aurora', value: 'aurora', bg: 'linear-gradient(135deg, #2D1A4A, #0F2D4A)' },
  { label: 'Oscuro', value: 'dark',   bg: '#0a0a0f' },
  { label: 'AMOLED', value: 'amoled', bg: '#000000' },
];

function Section({ title, emoji, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10, paddingLeft: 4 }}>
        {emoji} {title}
      </div>
      <div style={{ background: COLORS.card, borderRadius: 16, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, hint, children, last }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: last ? 'none' : `1px solid ${COLORS.border}` }}>
      <div style={{ flex: 1, marginRight: 12 }}>
        <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{label}</div>
        {hint && <div style={{ color: COLORS.textDim, fontSize: 11, marginTop: 2 }}>{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{ width: 46, height: 26, borderRadius: 13, background: value ? COLORS.green : COLORS.bg, border: `2px solid ${value ? COLORS.green : COLORS.border}`, position: 'relative', cursor: 'pointer', transition: 'all 0.25s ease', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 2, left: value ? 20 : 2, width: 18, height: 18, borderRadius: 9, background: value ? '#fff' : COLORS.textMuted, transition: 'left 0.25s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />
    </div>
  );
}

function NumInput({ value, onChange, suffix }) {
  const [local, setLocal] = React.useState(String(value ?? ''));
  React.useEffect(() => { setLocal(String(value ?? '')); }, [value]);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input type="text" inputMode="decimal" value={local} onChange={e => setLocal(e.target.value)}
        onBlur={() => { const n = parseFloat(local); onChange(isNaN(n) ? '' : n); }}
        style={{ width: 72, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '6px 8px', color: COLORS.accent, fontSize: 15, fontWeight: 700, textAlign: 'center', outline: 'none' }} />
      {suffix && <span style={{ color: COLORS.textDim, fontSize: 11 }}>{suffix}</span>}
    </div>
  );
}

function TxtInput({ value, onChange, placeholder }) {
  return (
    <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ width: 120, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '6px 10px', color: COLORS.text, fontSize: 14, outline: 'none', textAlign: 'right' }} />
  );
}

// ── Control de periodo (bloqueo de progresión / descarga) ────────────────────
// Presets rápidos (1 sem / 2 sem / 1 mes) + selector de fecha exacta.
// Si el periodo está activo muestra fecha fin + días restantes + botón desactivar.
function PeriodoControl({ emoji, titulo, hint, color, hasta, activo, onActivar, onCancelar, t, last, children }) {
  const [fecha, setFecha] = React.useState('');
  const fechaMin = new Date(Date.now() + 86400000).toISOString().split('T')[0]; // mañana
  const sumarDias = (n) => new Date(Date.now() + n * 86400000).toISOString().split('T')[0];
  const PRESETS = [
    { label: t('settings.recovery.week1'), dias: 7 },
    { label: t('settings.recovery.week2'), dias: 14 },
    { label: t('settings.recovery.month1'), dias: 30 },
  ];
  const diasRestantes = activo
    ? Math.max(1, Math.round((new Date(hasta) - new Date(new Date().toISOString().split('T')[0])) / 86400000) + 1)
    : 0;

  return (
    <div style={{ padding: '14px 16px', borderBottom: last ? 'none' : `1px solid ${COLORS.border}` }}>
      <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{emoji} {titulo}</div>
      <div style={{ color: COLORS.textDim, fontSize: 11, marginTop: 2, marginBottom: 10 }}>{hint}</div>
      {activo ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `${color}14`, border: `1px solid ${color}45`, borderRadius: 10, padding: '10px 12px' }}>
          <div>
            <div style={{ color, fontSize: 13, fontWeight: 800 }}>
              {t('settings.recovery.activeUntil', { fecha: fmtFecha(hasta) })}
            </div>
            <div style={{ color: COLORS.textDim, fontSize: 11, marginTop: 2 }}>
              {t('settings.recovery.daysLeft', { n: diasRestantes })}
            </div>
          </div>
          <button onClick={onCancelar}
            style={{ padding: '7px 12px', borderRadius: 8, background: 'transparent', border: '1px solid #ff2d5566', color: '#ff2d55', fontSize: 11, fontWeight: 800, cursor: 'pointer', flexShrink: 0 }}>
            {t('settings.recovery.cancel')}
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {PRESETS.map(p => (
              <button key={p.dias} onClick={() => onActivar(sumarDias(p.dias))}
                style={{ padding: '7px 11px', borderRadius: 8, border: `1px solid ${color}50`, background: `${color}12`, color, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {p.label}
              </button>
            ))}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
              <input type="date" min={fechaMin} value={fecha} onChange={e => setFecha(e.target.value)}
                style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '6px 8px', color: fecha ? COLORS.text : COLORS.textMuted, fontSize: 12, outline: 'none', colorScheme: 'dark' }} />
              <button onClick={() => { if (fecha) { onActivar(fecha); setFecha(''); } }} disabled={!fecha}
                style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: fecha ? color : COLORS.border, color: '#fff', fontSize: 11, fontWeight: 800, cursor: fecha ? 'pointer' : 'default', opacity: fecha ? 1 : 0.5 }}>
                OK
              </button>
            </div>
          </div>
        </>
      )}
      {children}
    </div>
  );
}

// ── SettingsScreen recibe settings y onSettingsChange desde App.js ──────────
// La BD es la única fuente de verdad. No se usa localStorage aquí.
export default function SettingsScreen({ settings, onSettingsChange, currentUser, onHandleChange }) {
  const { t, i18n } = useTranslation();
  const i18nLng = i18n.language || 'es';
  const [s, setS] = useState(settings);
  const [saved, setSaved] = useState(false);
  const [handle, setHandle] = useState(currentUser?.handle || '');
  const [handleStatus, setHandleStatus] = useState(null); // null | {available, reason}
  const [handleSaving, setHandleSaving] = useState(false);
  const [watchToken, setWatchToken] = useState(null);
  const [showMedicionPopup, setShowMedicionPopup] = useState(false);
  const [medicionFotos, setMedicionFotos] = useState([]);
  const [medicionAnalyzing, setMedicionAnalyzing] = useState(false);
  const [medicionResult, setMedicionResult] = useState(null);
  const [medicionError, setMedicionError] = useState('');
  const medicionInputRef = useRef(null);
  const [medicionHistorial, setMedicionHistorial] = useState([]);
  const [medicionSelFecha, setMedicionSelFecha] = useState(null);
  const [medicionCalMes, setMedicionCalMes] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const handleRef = React.useRef(null);

  // Auto-focus handle input if URL has ?focus=handle
  useEffect(() => {
    const focus = new URLSearchParams(window.location.search).get('focus');
    if (focus === 'handle') {
      setTimeout(() => { handleRef.current?.focus(); handleRef.current?.select(); }, 300);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Si el padre recarga los settings (cambio de usuario), sincronizar
  useEffect(() => { setS(settings); }, [settings]);
  useEffect(() => { setHandle(currentUser?.handle || ''); setHandleStatus(null); }, [currentUser]);

  const checkHandle = async (val) => {
    const h = val.trim().toLowerCase();
    if (!h || h === currentUser?.handle) { setHandleStatus(null); return; }
    const r = await fetch(`/api/auth/handle/check?handle=${encodeURIComponent(h)}`).catch(() => null);
    if (!r) return;
    const d = await r.json();
    setHandleStatus(d);
  };

  const saveHandle = async () => {
    const h = handle.trim().toLowerCase();
    if (!h || h === currentUser?.handle) return;
    if (handleStatus && !handleStatus.available) return;
    setHandleSaving(true);
    const r = await fetch('/api/auth/handle', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ handle: h }),
    }).catch(() => null);
    setHandleSaving(false);
    if (r?.ok) { const d = await r.json(); if (d.ok) { onHandleChange(d.handle); setHandleStatus(null); setSaved(true); } }
  };

  const update = (key, val) => {
    let next = { ...s, [key]: val };
    if (key === 'goal_kcal' || key === 'nivel_actividad') {
      const kcal = key === 'goal_kcal' ? (parseFloat(val) || 0) : (parseFloat(s.goal_kcal) || 0);
      const nivel = key === 'nivel_actividad' ? val : s.nivel_actividad;
      const m = calcMacros(kcal, nivel);
      next = { ...next, goal_proteina: m.proteina, goal_carbos: m.carbos, goal_grasa: m.grasa };
    }
    setS(next);
    onSettingsChange(next);
    setSaved(true);
  };

  const updateMulti = (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    onSettingsChange(next);
    setSaved(true);
  };

  // ── Cuidado y recuperación ─────────────────────────────────────────────────
  // Snapshot de la rutina activa al activar bloqueo/descarga: techo de peso,
  // reps y series por ejercicio que no se podrá superar mientras dure el periodo
  const snapshotBaseline = async () => {
    try {
      const r = await fetch(`${API}/rutinas/activa`, { credentials: 'include' });
      const rutina = await r.json();
      const base = {};
      Object.values(rutina?.dias || {}).forEach(exs => {
        (Array.isArray(exs) ? exs : []).forEach(e => {
          if (e?.name && base[e.name] === undefined) {
            base[e.name] = { weight: e.weight ?? '', reps: e.reps ?? '', sets: e.sets ?? '' };
          }
        });
      });
      return Object.keys(base).length ? base : null;
    } catch { return null; }
  };

  const activarPeriodo = async (key, fecha) => {
    if (!fecha || fecha < hoyISO()) return;
    const patch = { [key]: fecha };
    // Solo capturar baseline si no había ya un periodo activo (conservar el techo original)
    if (!bloqueoActivo(s)) patch.bloqueo_baseline = await snapshotBaseline();
    updateMulti(patch);
  };

  const cancelarPeriodo = (key) => {
    const patch = { [key]: '' };
    const otroActivo = key === 'descarga_hasta'
      ? (!!s.bloqueo_progresion_hasta && hoyISO() <= s.bloqueo_progresion_hasta)
      : descargaActiva(s);
    if (!otroActivo) patch.bloqueo_baseline = null;
    updateMulti(patch);
  };

  useEffect(() => {
    if (saved) {
      const t = setTimeout(() => setSaved(false), 2000);
      return () => clearTimeout(t);
    }
  }, [saved]);

  const handleSave = () => {
    onSettingsChange(s);
    setSaved(true);
  };

  const loadMedicionHistorial = async () => {
    try {
      const r = await fetch(`${API}/mediciones-corporales`, { credentials: 'include' });
      const d = await r.json();
      if (Array.isArray(d)) {
        setMedicionHistorial(d);
        if (d.length > 0) {
          const ultima = d[0].fecha?.split('T')[0];
          setMedicionSelFecha(ultima);
          if (ultima) {
            const dt = new Date(ultima);
            setMedicionCalMes({ y: dt.getFullYear(), m: dt.getMonth() });
          }
        }
      }
    } catch {}
  };

  const handleMedicionFoto = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - medicionFotos.length);
    const previews = await Promise.all(files.map(f => new Promise(res => {
      const reader = new FileReader();
      reader.onload = ev => res({ file: f, preview: ev.target.result });
      reader.readAsDataURL(f);
    })));
    setMedicionFotos(prev => [...prev, ...previews].slice(0, 3));
    e.target.value = '';
  };

  const handleMedicionSubmit = async () => {
    setMedicionAnalyzing(true); setMedicionError('');
    try {
      const fd = new FormData();
      fd.append('fuente', medicionFotos.length > 0 ? 'bioimpedancia' : 'manual');
      medicionFotos.forEach(f => fd.append('fotos', f.file));
      const r = await fetch(`${API}/mediciones-corporales`, { method: 'POST', credentials: 'include', body: fd });
      const d = await r.json();
      if (d.ok) {
        setMedicionResult(d.datos);
        if (d.datos?.peso) update('peso', d.datos.peso);
      } else setMedicionError(t('settings.measurement.saveError'));
    } catch { setMedicionError(t('settings.measurement.connectionError')); }
    setMedicionAnalyzing(false);
  };

  return (
    <>
    {showMedicionPopup && (() => {
      // Días con datos este mes
      const diasConDatos = new Set(medicionHistorial.map(m => m.fecha?.split('T')[0]).filter(Boolean));
      const { y, m: mes } = medicionCalMes;
      const primerDia = new Date(y, mes, 1).getDay();
      const diasEnMes = new Date(y, mes + 1, 0).getDate();
      const MESES = new Array(12).fill(0).map((_, i) => new Intl.DateTimeFormat(i18nLng, { month: 'long' }).format(new Date(2000, i, 1)));
      const pad = (n) => String(n).padStart(2, '0');

      // Medición seleccionada
      const medSel = medicionSelFecha
        ? medicionHistorial.find(m => m.fecha?.split('T')[0] === medicionSelFecha)
        : medicionHistorial[0];

      const FIELD_LABELS = { peso: t('settings.measurement.fields.peso'), grasa_corporal: t('settings.measurement.fields.grasa_corporal'), masa_muscular: t('settings.measurement.fields.masa_muscular'), agua_corporal: t('settings.measurement.fields.agua_corporal'), masa_osea: t('settings.measurement.fields.masa_osea'), bmr: 'BMR', imc: 'IMC', edad_metabolica: t('settings.measurement.fields.edad_metabolica') };
      const FIELD_UNITS = { peso: 'kg', grasa_corporal: '%', masa_muscular: 'kg', agua_corporal: '%', masa_osea: 'kg', bmr: 'kcal', imc: '', edad_metabolica: 'años' };

      return (
        <div onClick={e => { if (e.target === e.currentTarget) setShowMedicionPopup(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0 0 env(safe-area-inset-bottom)' }}>
          <div style={{ width: '100%', maxWidth: 430, background: COLORS.surface, borderRadius: '20px 20px 0 0', padding: '20px 20px 32px', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: COLORS.text, margin: 0, fontSize: 17, fontWeight: 800 }}>{t('settings.measurement.title')}</h3>
              <button onClick={() => setShowMedicionPopup(false)} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Última medición */}
            {medSel && (
              <div style={{ background: `${COLORS.blue}10`, border: `1px solid ${COLORS.blue}30`, borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
                <p style={{ color: COLORS.blue, fontWeight: 800, fontSize: 11, letterSpacing: 1, margin: '0 0 10px' }}>
                  {fmtFecha(medicionSelFecha || medSel.fecha?.split('T')[0])}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
                  {Object.entries(FIELD_LABELS).map(([k, label]) => medSel[k] != null && (
                    <div key={k}>
                      <span style={{ color: COLORS.textMuted, fontSize: 10 }}>{label} </span>
                      <span style={{ color: COLORS.text, fontSize: 14, fontWeight: 700 }}>{medSel[k]}{FIELD_UNITS[k]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Calendario */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <button onClick={() => setMedicionCalMes(({ y, m }) => m === 0 ? { y: y-1, m: 11 } : { y, m: m-1 })}
                  style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 18, cursor: 'pointer', padding: '0 6px' }}>‹</button>
                <span style={{ color: COLORS.text, fontSize: 13, fontWeight: 700 }}>{MESES[mes]} {y}</span>
                <button onClick={() => setMedicionCalMes(({ y, m }) => m === 11 ? { y: y+1, m: 0 } : { y, m: m+1 })}
                  style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 18, cursor: 'pointer', padding: '0 6px' }}>›</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, textAlign: 'center' }}>
                {['D','L','M','X','J','V','S'].map(d => (
                  <div key={d} style={{ color: COLORS.textDim, fontSize: 9, fontWeight: 700, padding: '2px 0' }}>{d}</div>
                ))}
                {Array(primerDia === 0 ? 0 : primerDia).fill(null).map((_, i) => <div key={'e'+i} />)}
                {Array.from({ length: diasEnMes }, (_, i) => {
                  const dia = i + 1;
                  const fechaStr = `${y}-${pad(mes+1)}-${pad(dia)}`;
                  const tieneData = diasConDatos.has(fechaStr);
                  const isSelected = medicionSelFecha === fechaStr;
                  const today = new Date().toISOString().split('T')[0];
                  const isToday = fechaStr === today;
                  return (
                    <div key={dia} onClick={() => tieneData && setMedicionSelFecha(fechaStr)}
                      style={{ padding: '4px 0', cursor: tieneData ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <span style={{
                        width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isSelected ? COLORS.blue : isToday ? `${COLORS.blue}20` : 'transparent',
                        border: isToday && !isSelected ? `1px solid ${COLORS.blue}50` : 'none',
                        color: isSelected ? '#fff' : tieneData ? COLORS.text : COLORS.textDim,
                        fontSize: 12, fontWeight: tieneData ? 700 : 400,
                      }}>{dia}</span>
                      {tieneData && <div style={{ width: 4, height: 4, borderRadius: '50%', background: isSelected ? COLORS.blue : COLORS.blue + '90' }} />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Separador + subir foto */}
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 16, marginBottom: 12 }}>
              <p style={{ color: COLORS.textMuted, fontSize: 11, margin: '0 0 12px' }}>{t('settings.measurement.addHint')}</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {medicionFotos.map((f, i) => (
                  <div key={i} style={{ position: 'relative', width: 72, height: 72, borderRadius: 10, overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
                    <img src={f.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button onClick={() => setMedicionFotos(prev => prev.filter((_, j) => j !== i))}
                      style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer' }}>✕</button>
                  </div>
                ))}
                {medicionFotos.length < 3 && (
                  <button onClick={() => medicionInputRef.current?.click()}
                    style={{ width: 72, height: 72, borderRadius: 10, background: COLORS.bg, border: `1px dashed ${COLORS.blue}60`, color: COLORS.blue, fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📷</button>
                )}
                <input ref={medicionInputRef} type="file" accept="image/*" multiple onChange={handleMedicionFoto} style={{ display: 'none' }} />
              </div>
              {medicionResult ? (
                <div style={{ background: `${COLORS.green}10`, border: `1px solid ${COLORS.green}40`, borderRadius: 12, padding: 12, marginBottom: 12 }}>
                  <p style={{ color: COLORS.green, fontWeight: 800, fontSize: 12, margin: '0 0 6px' }}>{t('settings.measurement.savedLabel')}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                    {Object.entries(FIELD_LABELS).map(([k, label]) => medicionResult[k] != null && (
                      <div key={k} style={{ color: COLORS.textMuted, fontSize: 11 }}>{label}: <span style={{ color: COLORS.text, fontWeight: 700 }}>{medicionResult[k]}{FIELD_UNITS[k]}</span></div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {medicionError && <p style={{ color: '#ff2d55', fontSize: 12, marginBottom: 8 }}>{medicionError}</p>}
                  <button onClick={async () => { if (!window.confirm('00bfCerrar sesi00f3n?')) return; 
                    await handleMedicionSubmit();
                    await loadMedicionHistorial();
                  }} disabled={medicionAnalyzing || medicionFotos.length === 0}
                    style={{ width: '100%', padding: 13, borderRadius: 12, background: medicionAnalyzing || medicionFotos.length === 0 ? COLORS.bg : `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.blue}cc)`, border: 'none', color: medicionFotos.length === 0 ? COLORS.textMuted : '#fff', fontSize: 14, fontWeight: 800, cursor: medicionFotos.length === 0 ? 'default' : 'pointer' }}>
                    {medicionAnalyzing ? t('settings.measurement.analyzingBtn') : t('settings.measurement.analyzeBtn')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    })()}
    <div style={{ padding: '0 20px calc(120px + env(safe-area-inset-bottom))' }}>
      <div style={{ paddingTop: 'calc(var(--sat, env(safe-area-inset-top, 0px)) + 14px)', marginBottom: 28 }}>
        <p style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 4px' }}>{t('settings.subtitle')}</p>
        <h1 style={{ color: COLORS.text, fontSize: 32, fontWeight: 900, margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t('settings.title')}</h1>
      </div>

      <Section title={t('settings.sections.profile')} emoji="👤">
        <Row label={t('settings.profile.name')}><TxtInput value={s.nombre} onChange={v => update('nombre', v)} placeholder={t('settings.profile.namePlaceholder')} /></Row>
        <Row label={t('settings.profile.username')} hint={t('settings.profile.usernameHint')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ position: 'relative' }}>
              <input ref={handleRef} type="text" value={handle}
                onChange={e => { setHandle(e.target.value); setHandleStatus(null); }}
                onBlur={() => checkHandle(handle)}
                style={{ width: 110, background: '#0a0a0f', border: `1px solid ${handleStatus ? (handleStatus.available ? '#2ecc71' : '#ff2d55') : '#2a2a3a'}`, borderRadius: 8, padding: '6px 8px', color: handleStatus?.available ? '#2ecc71' : '#f0f0f8', fontSize: 13, fontWeight: 700, outline: 'none', textAlign: 'center' }} />
              {handleStatus && !handleStatus.available && (
                <div style={{ position: 'absolute', right: 0, top: '110%', background: '#1a1a26', border: '1px solid #ff2d55', borderRadius: 6, padding: '4px 8px', fontSize: 10, color: '#ff2d55', whiteSpace: 'nowrap', zIndex: 10 }}>{handleStatus.reason}</div>
              )}
            </div>
            <button onClick={saveHandle} disabled={handleSaving || !handle.trim() || handle.trim().toLowerCase() === currentUser?.handle || (handleStatus && !handleStatus.available)}
              style={{ padding: '6px 10px', borderRadius: 8, border: 'none', background: '#ff6b35', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: (handleSaving || !handle.trim() || handle.trim().toLowerCase() === currentUser?.handle) ? 0.4 : 1 }}>
              {handleSaving ? '...' : 'OK'}
            </button>
          </div>
        </Row>
        <Row label={t('settings.profile.height')}><NumInput value={s.altura} onChange={v => update('altura', v)} suffix="cm" /></Row>
        <Row label={t('settings.profile.age')}><NumInput value={s.edad} onChange={v => update('edad', v)} suffix={t('settings.measurement.fields.edad_metabolica').includes('años') ? 'años' : 'años'} /></Row>
        <Row label={t('settings.profile.weight')} hint={t('settings.profile.weightHint')}><NumInput value={s.peso} onChange={v => update('peso', v)} suffix="kg" /></Row>
        <Row label={t('settings.profile.bodyMeasurement')} hint={t('settings.profile.bodyMeasurementHint')} last>
          <button onClick={() => { setShowMedicionPopup(true); setMedicionFotos([]); setMedicionResult(null); setMedicionError(''); loadMedicionHistorial(); }}
            style={{ padding: '7px 14px', borderRadius: 10, background: `${COLORS.blue}18`, border: `1px solid ${COLORS.blue}40`, color: COLORS.blue, fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
            {t('settings.profile.viewAdd')}
          </button>
        </Row>
      </Section>

      <Section title={t('settings.sections.goals')} emoji="🎯">
        <Row label={t('settings.goals.activityLevel')} hint={t('settings.goals.activityHint')}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {ACTIVITY_LEVELS.map(lv => (
              <button key={lv.id} onClick={() => update('nivel_actividad', lv.id)}
                style={{ padding: '5px 9px', borderRadius: 8, border: `1px solid ${s.nivel_actividad === lv.id ? COLORS.accent : COLORS.border}`, background: s.nivel_actividad === lv.id ? `${COLORS.accent}20` : 'transparent', color: s.nivel_actividad === lv.id ? COLORS.accent : COLORS.textMuted, fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {t(`settings.goals.levels.${lv.id}`)}
              </button>
            ))}
          </div>
        </Row>
        <Row label={t('settings.goals.calories')}><NumInput value={s.goal_kcal} onChange={v => update('goal_kcal', v)} suffix="kcal" /></Row>
        <Row label={t('settings.goals.protein')}><NumInput value={s.goal_proteina} onChange={v => update('goal_proteina', v)} suffix="g" /></Row>
        <Row label={t('settings.goals.carbs')}><NumInput value={s.goal_carbos} onChange={v => update('goal_carbos', v)} suffix="g" /></Row>
        <Row label={t('settings.goals.fat')} last><NumInput value={s.goal_grasa} onChange={v => update('goal_grasa', v)} suffix="g" /></Row>
      </Section>

      <Section title={t('settings.sections.gym')} emoji="🏋️">
        <Row label={t('settings.gym.defaultRest')} hint={t('settings.gym.defaultRestHint')}>
          <NumInput value={s.descanso_defecto} onChange={v => update('descanso_defecto', v)} suffix="seg" />
        </Row>
        <Row label={t('settings.gym.vibration')} hint={t('settings.gym.vibrationHint')}>
          <Toggle value={s.vibracion} onChange={v => update('vibracion', v)} />
        </Row>
        <Row label={t('settings.gym.notifications')} hint={t('settings.gym.notificationsHint')} last>
          <Toggle value={s.notificaciones} onChange={v => update('notificaciones', v)} />
        </Row>
      </Section>

      <Section title={t('settings.sections.recovery')} emoji="🩹">
        <PeriodoControl
          emoji="🔒"
          titulo={t('settings.recovery.block')}
          hint={t('settings.recovery.blockHint')}
          color="#4fc3f7"
          hasta={s.bloqueo_progresion_hasta}
          activo={!!s.bloqueo_progresion_hasta && hoyISO() <= s.bloqueo_progresion_hasta}
          onActivar={fecha => activarPeriodo('bloqueo_progresion_hasta', fecha)}
          onCancelar={() => cancelarPeriodo('bloqueo_progresion_hasta')}
          t={t}
        />
        <PeriodoControl
          emoji="🪶"
          titulo={t('settings.recovery.deload')}
          hint={t('settings.recovery.deloadHint', { pct: s.descarga_pct || 30 })}
          color="#2ecc71"
          hasta={s.descarga_hasta}
          activo={descargaActiva(s)}
          onActivar={fecha => activarPeriodo('descarga_hasta', fecha)}
          onCancelar={() => cancelarPeriodo('descarga_hasta')}
          t={t}
          last
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
            <div style={{ flex: 1, marginRight: 12 }}>
              <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>{t('settings.recovery.deloadPct')}</div>
              <div style={{ color: COLORS.textDim, fontSize: 11, marginTop: 2 }}>{t('settings.recovery.deloadPctHint')}</div>
            </div>
            <NumInput value={s.descarga_pct ?? 30} onChange={v => update('descarga_pct', Math.min(60, Math.max(5, v || 30)))} suffix="%" />
          </div>
          {descargaActiva(s) && (
            <div style={{ color: COLORS.textDim, fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>
              {t('settings.recovery.implicitNote')}
            </div>
          )}
        </PeriodoControl>
      </Section>

      <Section title={t('settings.sections.appearance')} emoji="🎨">
        <Row label={t('settings.appearance.accentColor')} hint={t('settings.appearance.accentColorHint')}>
          <div style={{ display: 'flex', gap: 6 }}>
            {ACCENT_OPTIONS.map(opt => (
              <div key={opt.value} onClick={() => update('accent_color', opt.value)} title={opt.label}
                style={{ width: 26, height: 26, borderRadius: 8, background: opt.value, border: s.accent_color === opt.value ? '2px solid #fff' : '2px solid transparent', cursor: 'pointer', boxShadow: s.accent_color === opt.value ? `0 0 8px ${opt.value}` : 'none', transition: 'all 0.2s' }} />
            ))}
          </div>
        </Row>
        <Row label={t('settings.appearance.theme')}>
          <div style={{ display: 'flex', gap: 6 }}>
            {TEMA_OPTIONS.map(opt => (
              <div key={opt.value} onClick={() => update('tema', opt.value)} title={opt.label}
                style={{ width: 36, height: 26, borderRadius: 8, background: opt.bg, border: s.tema === opt.value ? `2px solid ${COLORS.accent}` : '2px solid transparent', cursor: 'pointer', fontSize: 8, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                {s.tema === opt.value ? '✓' : ''}
              </div>
            ))}
          </div>
        </Row>
        <Row label={t('settings.appearance.auroraColor')} hint={t('settings.appearance.auroraColorHint')} last>
          <div style={{ display: 'flex', gap: 6 }}>
            {BG_TINT_OPTIONS.map(opt => (
              <div key={opt.id} onClick={() => update('bg_tint', opt.id)} title={opt.label}
                style={{ width: 26, height: 26, borderRadius: 8, background: opt.color + '33', border: `2px solid ${s.bg_tint === opt.id ? opt.color : opt.color + '40'}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', boxShadow: s.bg_tint === opt.id ? `0 0 8px ${opt.color}80` : 'none' }}>
                {s.bg_tint === opt.id && <span style={{ color: opt.color, fontSize: 11, fontWeight: 900, lineHeight: 1 }}>✓</span>}
              </div>
            ))}
          </div>
        </Row>
      </Section>

      <Section title={t('settings.sections.app')} emoji="📱">
        <Row label={t('settings.app.lockRotation')} hint={t('settings.app.lockRotationHint')}>
          <Toggle value={s.bloquear_rotacion} onChange={v => update('bloquear_rotacion', v)} />
        </Row>
        <Row label={t('settings.app.shakeUndo')}
          hint={s.shake_undo ? t('settings.app.shakeUndoHintOn') : t('settings.app.shakeUndoHintOff')}
          last>
          <Toggle value={s.shake_undo} onChange={v => update('shake_undo', v)} />
        </Row>
      </Section>

      <Section title={t('settings.sections.watch')} emoji="⌚">
        <div style={{ padding: '12px 0' }}>
          <p style={{ color: COLORS.textMuted, fontSize: 12, margin: '0 0 12px', lineHeight: 1.5 }}>
            {t('settings.watch.description')} <span style={{ color: COLORS.accent, fontFamily: 'monospace' }}>?token=</span>
          </p>
          {!watchToken ? (
            <button
              onClick={async () => {
                const res = await fetch(API + '/auth/watch-token', { credentials: 'include' }).then(r => r.json()).catch(() => null);
                if (res?.watch_token) {
                  setWatchToken(res.watch_token);
                  setTimeout(() => setWatchToken(null), 30000);  // Auto-hide after 30s
                }
              }}
              style={{ padding: '10px 16px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: `1px solid rgba(255,255,255,0.12)`, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}
            >
              {t('settings.watch.viewToken')}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, color: COLORS.text, wordBreak: 'break-all', border: `1px solid rgba(255,255,255,0.1)` }}>
                {watchToken}
              </div>
              <button
                onClick={() => { navigator.clipboard?.writeText(watchToken); }}
                style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: `1px solid rgba(255,255,255,0.12)`, color: '#fff', cursor: 'pointer', fontSize: 18, flexShrink: 0 }}
                title={t('settings.watch.copyToken')}
              >📋</button>
            </div>
          )}
        </div>
      </Section>

      <Section title={t('settings.sections.language')} emoji="🌍">
        <div style={{ padding: '12px 16px' }}>
          <LanguageSwitcher />
        </div>
      </Section>

      <Section title={t('settings.sections.info')} emoji="ℹ️">
        <Row label={t('common.version')} last>
          <span style={{ color: COLORS.textMuted, fontSize: 13 }}>Fitmemo 2.0</span>
        </Row>
      </Section>

      <button onClick={handleSave} style={{ width: '100%', padding: 18, borderRadius: 16, marginTop: 8, background: saved ? `linear-gradient(135deg, ${COLORS.green}, #27ae60)` : `linear-gradient(135deg, ${COLORS.accent}, #e55a2b)`, border: 'none', color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: `0 8px 24px ${COLORS.accentGlow}`, transition: 'background 0.3s ease' }}>
        {saved ? t('settings.savedBtn') : t('settings.saveBtn')}
      </button>

      <button onClick={async () => { if (!window.confirm('00bfCerrar sesi00f3n?')) return; 
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
        if ('caches' in window) { const keys = await caches.keys(); await Promise.all(keys.map(k => caches.delete(k))); }
        window.location.reload(true);
      }} style={{ width: '100%', padding: 16, borderRadius: 16, marginTop: 12, marginBottom: 8, background: 'transparent', border: `1px solid #ff2d5544`, color: '#ff2d55', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
        {t('settings.logout')}
      </button>
    </div>
    </>
  );
}
